import { useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Popover } from "@mui/material";
import { AiOutlinePlus } from "react-icons/ai";
import { BsEye, BsPencil, BsTrash } from "react-icons/bs";
import { MdUndo } from "react-icons/md";
import PayrollCompanyLayout from "../../../components/layouts/PayrollCompanyLayout";
import DeleteModal from "../../../components/modals/DeleteModal";
import TableFilter from "../../../components/table/filter/TableFilter";
import ManageTableColumns from "../../../components/table/ManageTableColumns";
import TanStackEmployeeTable from "../../../components/table/TanStackEmployeeTable";
import { APP_CONFIG } from "../../../helpers/appHelpers";
import { useColumnVisibilityModel } from "../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
	useValidateUserPermissions,
} from "../../../helpers/usePermissionManager";
import { EmployeeOrderBy } from "../../../models/employee.dto";
import { PayPeriodOrderBy } from "../../../models/pay-period.dto";
import {
	SalaryAdjustmentDto,
	SalaryAdjustmentOrderBy,
	SalaryAdjustmentStatus,
} from "../../../models/salary-adjustment.dto";
import { TableId } from "../../../models/table-configuration.dto";
import { useGetEmployee } from "../../../services/employee.service";
import { useGetPayPeriod } from "../../../services/pay-period.service";
import {
	useDeleteSalaryAdjustment,
	useGetSalaryAdjustment,
	useReverseSalaryAdjustment,
	isAdjustmentInProgress,
} from "../../../services/salary-adjustment.service";
import useCompanyStore from "../../../state-management/useCompanyStore";
import SalaryAdjustmentConfirmModal from "./modals/SalaryAdjustmentConfirmModal";
import SalaryAdjustmentDetailsModal from "./modals/SalaryAdjustmentDetailsModal";
import SalaryAdjustmentModalForm from "./modals/SalaryAdjustmentModalForm";
import {
	SalaryAdjustmentTableMeta,
	salaryAdjustmentColumns,
	salaryAdjustmentColumnVisibility,
} from "./tanstack-columns";

const PAGE_SIZE = 10;

const SalaryAdjustments = () => {
	const hasPermission = useValidateUserPermissions(
		[PERMISSIONS.SALARY_READ, PERMISSIONS.SALARY_WRITE],
		"OR",
	);
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const canWrite = checkUserPermissionsAnd([
		PERMISSIONS.PAYROLL_WRITE,
		PERMISSIONS.SALARY_WRITE,
	]);
	const [searchParams] = useSearchParams();
	const tableAPIRef = useRef<any>(null);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [selected, setSelected] = useState<SalaryAdjustmentDto | null>(null);
	const [openFormModal, setOpenFormModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openDetailsModal, setOpenDetailsModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [openReverseModal, setOpenReverseModal] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [reversing, setReversing] = useState(false);

	const { columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(salaryAdjustmentColumnVisibility);

	// Queried directly (instead of useManageTableWithSearchParams) so the
	// list's polling refetches flow into the table while adjustments are applied
	const page = Number(searchParams.get("page")) || 1;
	const isFiltered = !!searchParams.get("filter");
	const payPeriodFilter = isFiltered
		? Number(searchParams.get("payPeriodId")) || undefined
		: undefined;
	const orderBy =
		(isFiltered &&
			(searchParams.get("orderBy") as SalaryAdjustmentOrderBy | null)) ||
		SalaryAdjustmentOrderBy.CREATED_AT_DESC;

	const { data: salaryAdjustments, isLoading } = useGetSalaryAdjustment(
		currentCompany?.id as number,
		{
			page,
			limit: PAGE_SIZE,
			orderBy,
			payPeriodId: payPeriodFilter,
		},
	);

	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: PayPeriodOrderBy.START_DATE_DESC,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const payPeriodCodes = useMemo(
		() =>
			(payPeriods?.data || []).reduce<Record<number, string>>(
				(acc, payPeriod) => ({ ...acc, [payPeriod.id]: payPeriod.code }),
				{},
			),
		[payPeriods],
	);
	const tableMeta: SalaryAdjustmentTableMeta = { payPeriodCodes };

	const { mutate: deleteSalaryAdjustment } = useDeleteSalaryAdjustment();
	const { mutate: reverseSalaryAdjustment } = useReverseSalaryAdjustment();

	const handleRowAction = (row: SalaryAdjustmentDto, target: HTMLElement) => {
		setSelected(row);
		setAnchorEl(target);
	};

	const closeActions = () => setAnchorEl(null);

	const handleOpenNewForm = () => {
		setSelected(null);
		setIsEditForm(false);
		setOpenFormModal(true);
	};

	const handleDeleteMethod = () => {
		if (!selected) return;
		setDeleting(true);

		deleteSalaryAdjustment(
			{ companyId: currentCompany?.id as number, id: selected.id },
			{
				onSuccess: () => setOpenDeleteModal(false),
				onSettled: () => setDeleting(false),
			},
		);
	};

	const handleReverseMethod = () => {
		if (!selected) return;
		setReversing(true);

		reverseSalaryAdjustment(
			{ companyId: currentCompany?.id as number, id: selected.id },
			{
				onSuccess: () => setOpenReverseModal(false),
				onSettled: () => setReversing(false),
			},
		);
	};

	const status = selected?.status;
	// Rows being applied can no longer be changed
	const inProgress = !!selected && isAdjustmentInProgress(selected);
	const canEdit =
		canWrite && status === SalaryAdjustmentStatus.PENDING && !inProgress;
	const canDelete =
		canWrite &&
		((status === SalaryAdjustmentStatus.PENDING && !inProgress) ||
			status === SalaryAdjustmentStatus.FAILED_PROCESSING ||
			status === SalaryAdjustmentStatus.EXPIRED);
	// Adjustments applied before undo existed have no appliedAt
	const canReverse =
		canWrite &&
		status === SalaryAdjustmentStatus.PPROCESSED_SUCCESSFULLY &&
		!!selected?.appliedAt;

	return (<>
		{hasPermission && (
			<PayrollCompanyLayout>
				<Helmet>
					<title>Salary Adjustments | Company Flow</title>
				</Helmet>
				<h1 className="font-bold text-xl mb-8">Salary Adjustments</h1>
				<div className="font-inter">
					<div className="flex justify-between items-center mt-4 mb-4">
						<div className="flex items-center gap-3">
							<TableFilter
								fields={[
									{
										title: "",
										fieldName: "",
										fieldType: "SORT",
										options: [
											{ title: "Date Created", fieldName: "createdAt" },
											{ title: "Date Modified", fieldName: "modifiedAt" },
										],
									},
									{
										title: "Pay Period",
										fieldName: "payPeriodId",
										fieldType: "SELECT_FIELD",
										options: payPeriods?.data || [],
										mapText: "code",
									},
								]}
							/>
							<ManageTableColumns
								columns={salaryAdjustmentColumns as any}
								defaultVisibilityModel={salaryAdjustmentColumnVisibility}
								visibilityModel={columnVisibilityModel}
								setVisibilityModel={setColumnVisibilityModel}
								tableApiRef={tableAPIRef}
							/>
						</div>
						<div className="flex items-center gap-4 font-medium">
							<PermissionChecker
								permissionRule="AND"
								permissions={[
									PERMISSIONS.PAYROLL_WRITE,
									PERMISSIONS.SALARY_WRITE,
								]}
							>
								<button
									onClick={handleOpenNewForm}
									className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
								>
									<AiOutlinePlus className="mr-2 text-lg" />
									New Salary Adjustment
								</button>
							</PermissionChecker>
						</div>
					</div>

					<TanStackEmployeeTable
						ref={tableAPIRef}
						tableId={TableId.SALARY_ADJUSTMENTS}
						data={salaryAdjustments?.data || []}
						columns={salaryAdjustmentColumns}
						loading={isLoading}
						columnVisibility={columnVisibilityModel}
						onColumnVisibilityChange={setColumnVisibilityModel}
						onRowClick={(row, event) =>
							handleRowAction(row, event.currentTarget as HTMLElement)
						}
						onActionClick={(row, event) =>
							handleRowAction(row, event.currentTarget as HTMLElement)
						}
						initialColumnVisibility={salaryAdjustmentColumnVisibility}
						pageSize={PAGE_SIZE}
						hasPermission={hasPermission}
						pageCount={salaryAdjustments?.pagination?.totalPages || 1}
						apiRef={tableAPIRef}
						meta={tableMeta}
					/>
				</div>

				<Popover
					id={anchorEl ? "salary-adjustment-actions" : undefined}
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					onClose={closeActions}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					PaperProps={{
						style: {
							borderRadius: 8,
							display: "flex",
							justifyContent: "center",
						},
					}}
				>
					<div className="w-full flex flex-col justify-center p-3">
						<div
							onClick={() => {
								closeActions();
								setOpenDetailsModal(true);
							}}
							className="table-action-popover"
						>
							<BsEye />
							<p className="text-2sm">View Details</p>
						</div>
						{canEdit && (
							<div
								onClick={() => {
									closeActions();
									setIsEditForm(true);
									setOpenFormModal(true);
								}}
								className="table-action-popover"
							>
								<BsPencil />
								<p className="text-2sm">Edit</p>
							</div>
						)}
						{canReverse && (
							<div
								onClick={() => {
									closeActions();
									setOpenReverseModal(true);
								}}
								className="table-action-popover"
							>
								<MdUndo />
								<p className="text-2sm">Undo</p>
							</div>
						)}
						{canDelete && (
							<div
								onClick={() => {
									closeActions();
									setOpenDeleteModal(true);
								}}
								className="table-action-popover"
							>
								<BsTrash />
								<p className="text-2sm">Delete</p>
							</div>
						)}
					</div>
				</Popover>

				<DeleteModal
					openDeleteModal={openDeleteModal}
					spanContent={selected?.name || ""}
					setOpenDeleteModal={setOpenDeleteModal}
					handleDeleteMethod={handleDeleteMethod}
					deleting={deleting}
				/>

				<SalaryAdjustmentConfirmModal
					open={openReverseModal}
					title="Undo salary adjustment?"
					confirmText="Yes, Undo"
					confirmingText="Undoing..."
					confirming={reversing}
					onConfirm={handleReverseMethod}
					onCancel={() => setOpenReverseModal(false)}
				>
					<p>
						This restores the previous basic pay of every employee and the
						previous amount of every notch changed by{" "}
						<span className="font-bold">{selected?.name}</span>.
					</p>
					<p>
						Any basic pay or notch amount that has been changed since the
						adjustment was applied will be skipped and left as it is.
					</p>
					<p>
						If a payroll has been run for the company since it was applied,
						it can no longer be undone and you will be told why.
					</p>
				</SalaryAdjustmentConfirmModal>

				{openFormModal && (
					<SalaryAdjustmentModalForm
						open={openFormModal}
						setOpen={setOpenFormModal}
						title={
							isEditForm
								? "Update Salary Adjustment"
								: "Add New Salary Adjustment"
						}
						isEditForm={isEditForm}
						adjustment={isEditForm ? selected : null}
						payPeriods={payPeriods?.data || []}
						employees={employees?.data || []}
					/>
				)}

				{openDetailsModal && selected && (
					<SalaryAdjustmentDetailsModal
						open={openDetailsModal}
						setOpen={setOpenDetailsModal}
						adjustment={selected}
						payPeriodCodes={payPeriodCodes}
						employees={employees?.data || []}
					/>
				)}
			</PayrollCompanyLayout>
		)}
	</>);
};

export default SalaryAdjustments;
